import { useEffect, useState } from 'react';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const testimonialSchema = z.object({
  student_name: z.string().min(2, 'Name must be at least 2 characters'),
  student_photo: z.string().optional(),
  feedback: z.string().min(10, 'Feedback must be at least 10 characters'),
  course_taken: z.string().min(2, 'Course is required'),
});

export default function AdminTestimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(testimonialSchema),
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/admin/testimonials/all`, getAuthHeaders());
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      if (editingTestimonial) {
        await axios.put(`${API}/testimonials/${editingTestimonial.testimonial_id}`, data, getAuthHeaders());
        toast.success('Testimonial updated successfully');
      } else {
        await axios.post(`${API}/testimonials`, data, getAuthHeaders());
        toast.success('Testimonial added successfully');
      }
      fetchTestimonials();
      setIsDialogOpen(false);
      reset();
      setEditingTestimonial(null);
    } catch (error) {
      console.error('Error saving testimonial:', error);
      toast.error(error.response?.data?.detail || 'Failed to save testimonial');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (testimonial) => {
    setEditingTestimonial(testimonial);
    setValue('student_name', testimonial.student_name);
    setValue('student_photo', testimonial.student_photo || '');
    setValue('feedback', testimonial.feedback);
    setValue('course_taken', testimonial.course_taken);
    setIsDialogOpen(true);
  };

  const handleDelete = async (testimonialId) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;

    try {
      await axios.delete(`${API}/testimonials/${testimonialId}`, getAuthHeaders());
      toast.success('Testimonial deleted successfully');
      fetchTestimonials();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      toast.error('Failed to delete testimonial');
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingTestimonial(null);
    reset();
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-brand-dark font-display">Manage Testimonials</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-testimonial-btn" onClick={() => { setEditingTestimonial(null); reset(); setIsDialogOpen(true); }} className="bg-brand-blue hover:bg-blue-600">
              <Plus className="h-4 w-4 mr-2" />
              Add Testimonial
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit(onSubmit)} data-testid="testimonial-form" className="space-y-4">
              <div>
                <Label htmlFor="student_name">Student Name *</Label>
                <Input id="student_name" data-testid="testimonial-name-input" {...register('student_name')} placeholder="Student name" />
                {errors.student_name && <p className="text-red-500 text-sm mt-1">{errors.student_name.message}</p>}
              </div>

              <div>
                <Label htmlFor="student_photo">Student Photo URL (Optional)</Label>
                <Input id="student_photo" data-testid="testimonial-photo-input" {...register('student_photo')} placeholder="https://example.com/photo.jpg" />
                {errors.student_photo && <p className="text-red-500 text-sm mt-1">{errors.student_photo.message}</p>}
              </div>

              <div>
                <Label htmlFor="course_taken">Course Taken *</Label>
                <Input id="course_taken" data-testid="testimonial-course-input" {...register('course_taken')} placeholder="e.g., GATE Preparation - CS" />
                {errors.course_taken && <p className="text-red-500 text-sm mt-1">{errors.course_taken.message}</p>}
              </div>

              <div>
                <Label htmlFor="feedback">Feedback *</Label>
                <Textarea id="feedback" data-testid="testimonial-feedback-input" {...register('feedback')} placeholder="Student's feedback" rows={4} />
                {errors.feedback && <p className="text-red-500 text-sm mt-1">{errors.feedback.message}</p>}
              </div>

              <Button type="submit" data-testid="testimonial-submit-btn" disabled={isSubmitting} className="w-full">
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : editingTestimonial ? (
                  'Update Testimonial'
                ) : (
                  'Add Testimonial'
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="inline-block h-8 w-8 animate-spin text-brand-blue" />
          <p className="mt-4 text-slate-600">Loading testimonials...</p>
        </div>
      ) : testimonials.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-slate-600">No testimonials yet. Add your first testimonial!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {testimonials.map((testimonial) => (
            <div key={testimonial.testimonial_id} data-testid={`testimonial-item-${testimonial.testimonial_id}`} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start flex-1">
                  {testimonial.student_photo && (
                    <img src={testimonial.student_photo} alt={testimonial.student_name} className="w-16 h-16 rounded-full object-cover mr-4" />
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-brand-dark">{testimonial.student_name}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        testimonial.status === 'approved' ? 'bg-green-100 text-green-700' :
                        testimonial.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {testimonial.status}
                      </span>
                    </div>
                    <p className="text-sm text-brand-blue mb-2">{testimonial.course_taken}</p>
                    <p className="text-slate-600 italic">"{testimonial.feedback}"</p>
                  </div>
                </div>
                <div className="flex gap-2 ml-4 flex-col">
                  {testimonial.status === 'pending' && (
                    <>
                      <Button
                        data-testid={`approve-testimonial-${testimonial.testimonial_id}`}
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/admin/testimonials/${testimonial.testimonial_id}/approve`, 
                              { status: 'approved' }, 
                              getAuthHeaders()
                            );
                            toast.success('Testimonial approved');
                            fetchTestimonials();
                          } catch (error) {
                            toast.error('Failed to approve');
                          }
                        }}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Approve
                      </Button>
                      <Button
                        data-testid={`reject-testimonial-${testimonial.testimonial_id}`}
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/admin/testimonials/${testimonial.testimonial_id}/approve`, 
                              { status: 'rejected' }, 
                              getAuthHeaders()
                            );
                            toast.success('Testimonial rejected');
                            fetchTestimonials();
                          } catch (error) {
                            toast.error('Failed to reject');
                          }
                        }}
                        size="sm"
                        variant="destructive"
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  <Button
                    data-testid={`edit-testimonial-${testimonial.testimonial_id}`}
                    onClick={() => handleEdit(testimonial)}
                    variant="outline"
                    size="sm"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    data-testid={`delete-testimonial-${testimonial.testimonial_id}`}
                    onClick={() => handleDelete(testimonial.testimonial_id)}
                    variant="destructive"
                    size="sm"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
